import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Why is my car overheating?",
  "When should I service my car?",
  "How can I reduce fuel consumption?",
  "What should I check before a long trip?",
];

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => localStorage.getItem("vp_session") || crypto.randomUUID());
  const endRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("vp_session", sessionId);
    api.get(`/assistant/history/${sessionId}`).then((r) => setMessages(r.data)).catch(() => {});
  }, [sessionId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg, session_id: sessionId });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(apiError(e));
      setMessages((m) => [...m, { role: "assistant", content: "No wahala — try that again in a moment." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] lg:h-[calc(100vh-6rem)]" data-testid="assistant-page">
      <PageHeader title="Ask VecPadi" subtitle="Your friendly car padi. Ask me anything." icon={MessageCircle} />

      <div className="flex-1 overflow-y-auto no-scrollbar space-y-4 pb-4">
        {messages.length === 0 && !sending && (
          <div className="rounded-3xl border border-zinc-200 bg-white p-6">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-50 text-emerald-600"><Sparkles size={22} /></div>
            <h3 className="mt-4 font-display text-lg font-semibold">How can I help your car today?</h3>
            <p className="text-sm text-muted-foreground">Tap a question or type your own.</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="assistant-suggestion" onClick={() => send(s)}
                  className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-left text-sm text-zinc-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-3xl px-4 py-3 text-sm leading-relaxed ${
              m.role === "user" ? "bg-emerald-500 text-white rounded-br-md" : "bg-white border border-zinc-200 text-zinc-800 rounded-bl-md"
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-3xl rounded-bl-md border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-400">
              <Loader2 className="animate-spin inline" size={16} /> VecPadi dey think…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="sticky bottom-0 flex gap-2 border-t border-zinc-200 bg-background pt-3">
        <Input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Ask VecPadi…" className="h-12 rounded-full flex-1" />
        <Button data-testid="assistant-send" type="submit" disabled={sending || !input.trim()}
          className="h-12 w-12 rounded-full bg-emerald-500 hover:bg-emerald-600 p-0">
          <Send size={18} />
        </Button>
      </form>
      <p className="mt-2 text-center text-[11px] text-zinc-400">VecPadi gives advice, not repairs. Always confirm with a certified mechanic.</p>
    </div>
  );
}
